import { OrdersGroup, OrdersSegment } from './gameDay-data.segmentations';
import { GameDayModel } from '../../models/gameday-model';
import {
  LabelExtractor,
  SizeExtractor,
  TrancheExtractor
} from '../mappers/gameDay.extractors';

class Part {
  id: string;
  amount = 0;
  orders: GameDayModel.Order[] = [];
  color = '';
}

class Tranche {
  id: string;
  amount = 0;
  orders: GameDayModel.Order[] = [];
  maturities: GameDayModel.Maturity[] = [];
  parts: Part[] = [];
  size = 0;
  topLabel: string[] = [];
  bottomLabel: string[] = [];
}

class TrancheGroup extends Tranche {
  tranches: Tranche[] = [];
}

function groupToPart(group: OrdersGroup): Part {
  const id = group.id;
  const amount = group.orders.reduce(
    (acc: number, order: GameDayModel.Order) => acc + order.amount,
    0
  );
  const orders = group.orders;
  return { id, amount, orders, color: undefined };
}

function segmentToTranche<T>(
  segment: OrdersSegment<T>,
  sizeExtractor: SizeExtractor<T>,
  topLabelExtractor: LabelExtractor<T>,
  bottomLabelExtractor: LabelExtractor<T>
): Tranche {
  const id = segment.id;
  const maturities = segment.maturities;
  const parts = segment.groups.map(groupToPart);
  const amount = parts.reduce(
    (acc: number, part: Part) => acc + part.amount,
    0
  );
  const orders = segment.orders;
  const size = sizeExtractor(segment);
  const topLabel = topLabelExtractor(segment);
  const bottomLabel = bottomLabelExtractor(segment);

  return {
    id,
    amount,
    orders,
    parts,
    size,
    topLabel,
    bottomLabel,
    maturities
  };
}

function trancheToTrancheGroup(
  tranche: Tranche,
  tranches: Tranche[]
): TrancheGroup {
  return { ...tranche, tranches };
}

function ordersSegmentsToTrancheGroups<T>(
  ordersSegments: OrdersSegment<T>[] = [],
  trancheExtractors: TrancheExtractor[] = []
): TrancheGroup[] {
  return ordersSegments.map((segment: OrdersSegment<T>) => {
    const innerSegments = segment.segments;
    const tranches = innerSegments.map(innerSegment =>
      segmentToTranche(
        innerSegment,
        trancheExtractors[1].size,
        trancheExtractors[1].top,
        trancheExtractors[1].bottom
      )
    );
    const tranche = segmentToTranche(
      segment,
      trancheExtractors[0].size,
      trancheExtractors[0].top,
      trancheExtractors[0].bottom
    );

    return trancheToTrancheGroup(tranche, tranches);
  });
}

function findTrancheById(trancheGroups: TrancheGroup[], id: string): Tranche {
  let foundTranche: Tranche;
  if (id) {
    foundTranche = trancheGroups.find(trancheGroup => trancheGroup.id === id);
    if (!foundTranche) {
      trancheGroups.some(trancheGroup => {
        foundTranche = trancheGroup.tranches.find(tranche => tranche.id === id);
        return !!foundTranche;
      });
    }
  }
  return foundTranche;
}

function getTrancheCompareId(tranche: Tranche): string {
  return `${tranche.id}${tranche.maturities &&
    tranche.maturities.map(m => m.id)}`;
}

function areSameTranches(
  trancheGroups1: Tranche[],
  trancheGroups2: Tranche[]
): boolean {
  if (!trancheGroups1 !== !trancheGroups2) {
    return false;
  }
  return (
    !!trancheGroups1 &&
    trancheGroups1.length === trancheGroups2.length &&
    trancheGroups1
      .map(getTrancheCompareId)
      .sort()
      .join('') ===
      trancheGroups2
        .map(getTrancheCompareId)
        .sort()
        .join('')
  );
}

export {
  ordersSegmentsToTrancheGroups,
  groupToPart,
  segmentToTranche,
  trancheToTrancheGroup,
  findTrancheById,
  areSameTranches,
  Part,
  Tranche,
  TrancheGroup
};
